/**
 * FeatureGate Component
 *
 * @description プラン機能制御コンポーネント - Issue #286対応
 * @features 機能アクセス制御、アップグレード誘導、Stripe統合
 */

import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Lock as LockIcon,
  TrendingUp as UpgradeIcon,
  Info as InfoIcon,
} from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { PLAN_LABELS } from '@/constants/subscriptionPlans';
import { trackFeatureBlockedByPlan } from '@/lib/analytics';
import { UpgradePrompt } from './UpgradePrompt';
import { FEATURE_DISPLAY_NAMES } from './featureDisplayNames';
import {
  applyPaidEntitlementBridgeToPlan,
  usePaidEntitlementBridge,
} from './paidEntitlementBridge';

/**
 * FeatureGate Props
 */
interface FeatureGateProps {
  /** 機能ID */
  feature: string;
  /** 機能名（表示用） */
  featureName?: string;
  /** 機能説明 */
  featureDescription?: string;
  /** 必要なプランレベル */
  requiredPlan?: 'free' | 'light' | 'standard' | 'premium';
  /** フォールバック表示（機能制限時） */
  fallback?: React.ReactNode;
  /** 子要素（機能が利用可能な場合に表示） */
  children: React.ReactNode;
  /** コンパクト表示 */
  compact?: boolean;
  /** アップグレード誘導を表示するか */
  showUpgradePrompt?: boolean;
  /** カスタムアップグレードハンドラー */
  onUpgrade?: () => void;
}

/**
 * プラン機能マッピング
 * @description プラン別の機能アクセス制御に使用
 * @exports useAuth.tsx (getFeaturesForPlan helper)
 *
 * `as const satisfies` で literal-narrow し、 `FeatureKey` 型を派生させる。
 * PostHog `feature_blocked_by_plan` の `feature` プロパティ SSOT として外部参照される。
 */
export const FEATURE_PLAN_MAP = {
  // Free プラン機能 (ADR-0014 Dashboard-First Architecture)
  dashboard: 'free',

  // Light プラン機能
  basic_reservation: 'light',
  customer_management: 'light',
  staff_management: 'light',
  basic_reports: 'light',

  // Light プラン機能（拡張）- Issue #1590 SSOT統一
  follow_up: 'light', // Issue #2256: フォローアップ自動化
  churn_alert: 'light', // Issue #2257: 離脱顧客アラート
  repeat: 'light', // Business PR #174: リピート作成 UI
  external_integrations: 'light', // HPB/楽天連携
  line_integration: 'light', // LINE連携
  advanced_reports: 'light', // 詳細レポート（売上分析）
  ai_analytics: 'free', // Issue #3314: AI分析は Free 含む全 plan (SSOT: docs/core/plan-comparison.md、 token quota は backend で plan 別 enforce)

  // Standard プラン機能
  inventory_management: 'standard',
  marketing_tools: 'standard',

  // Premium プラン機能
  custom_reports: 'premium',
  api_access: 'premium',
  multi_store: 'premium',
  custom_branding: 'premium',
  dedicated_support: 'premium',
  bpaas_services: 'premium',
} as const satisfies Record<string, 'free' | 'light' | 'standard' | 'premium'>;

/**
 * FEATURE_PLAN_MAP の key 集合。 PostHog 観測 event の `feature` プロパティ型として
 * 外部 (analytics.ts) が import する。 dashboard 上で SSOT 不一致を防ぐ。
 */
export type FeatureKey = keyof typeof FEATURE_PLAN_MAP;

/**
 * プランレベル階層
 * @description プランのアップグレード判定に使用 (0=free, 1=light, 2=standard, 3=premium)
 * @exports useAuth.tsx (getFeaturesForPlan helper)
 */
// eslint-disable-next-line react-refresh/only-export-components
export const PLAN_HIERARCHY: Record<string, number> = {
  free: 0,
  light: 1,
  standard: 2,
  premium: 3,
};

/**
 * プラン表示名 (SSOT: subscriptionPlans.ts の PLAN_LABELS を拡張)
 */
const PLAN_DISPLAY_NAMES: Record<string, string> = {
  free: 'フリープラン',
  ...PLAN_LABELS,
};

/**
 * FeatureGate Component
 *
 * 現在のプランに基づいて機能へのアクセスを制御
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  featureName,
  featureDescription,
  requiredPlan,
  fallback,
  children,
  compact = false,
  showUpgradePrompt = true,
  onUpgrade,
}) => {
  const currentPlan = useAuthStore((state) => state.currentPlan);
  const paidEntitlementBridge = usePaidEntitlementBridge();
  // Issue #1590: UpgradePrompt ダイアログの表示状態
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const mappedDisplayName =
    FEATURE_DISPLAY_NAMES[feature as keyof typeof FEATURE_DISPLAY_NAMES];
  const displayFeatureName = featureName ?? mappedDisplayName ?? feature;

  // 必要なプランレベルを決定
  // FEATURE_PLAN_MAP は as const literal map、 feature は string props なので index 型キャストで参照
  const minimumRequiredPlan =
    requiredPlan ||
    (
      FEATURE_PLAN_MAP as Record<
        string,
        'free' | 'light' | 'standard' | 'premium'
      >
    )[feature] ||
    'premium';

  const effectiveCurrentPlan = applyPaidEntitlementBridgeToPlan(
    currentPlan,
    paidEntitlementBridge
  );

  // プランレベルを取得（Plan名は既に統一されているため直接使用）
  // Issue #1497: フォールバックを free (0) に変更
  const currentPlanLevel = PLAN_HIERARCHY[effectiveCurrentPlan] || 0;
  const requiredPlanLevel = PLAN_HIERARCHY[minimumRequiredPlan];

  // 機能へのアクセス可否を判定
  const hasAccess = currentPlanLevel >= requiredPlanLevel;

  // plan gate に弾かれたら PostHog に capture (upgrade attribution 用)。
  // 同一 mount 中は (feature, currentPlan, minimumRequiredPlan) が変わらない限り 1 回だけ発火。
  // FEATURE_PLAN_MAP に含まれない feature 名 (動的渡しなど) は dashboard SSOT を汚さない
  // よう skip。 minimumRequiredPlan='free' は理論上 hasAccess=true 側に倒れるが型 narrow 用に明示 guard。
  useEffect(() => {
    if (hasAccess) return;
    if (minimumRequiredPlan === 'free') return;
    if (!(feature in FEATURE_PLAN_MAP)) return;
    trackFeatureBlockedByPlan({
      feature: feature as FeatureKey,
      current_plan: effectiveCurrentPlan,
      required_plan: minimumRequiredPlan,
      block_source: 'FeatureGate',
    });
  }, [hasAccess, feature, effectiveCurrentPlan, minimumRequiredPlan]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (featureName !== undefined) return;
    if (mappedDisplayName !== undefined) return;

    console.warn(
      `[FeatureGate] Missing display name for feature "${feature}". Add it to FEATURE_DISPLAY_NAMES or pass featureName explicitly.`
    );
  }, [feature, featureName, mappedDisplayName]);

  // Issue #1590: アップグレードハンドラー - UpgradePrompt ダイアログを表示
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // UpgradePrompt ダイアログを開く（Stripe Checkout への導線）
      setShowUpgradeDialog(true);
    }
  };

  // 機能が利用可能な場合
  if (hasAccess) {
    return <>{children}</>;
  }

  // カスタムフォールバックがある場合
  if (fallback) {
    return <>{fallback}</>;
  }

  // アップグレード誘導を表示しない場合
  if (!showUpgradePrompt) {
    return null;
  }

  // コンパクト表示
  if (compact) {
    return (
      <Alert data-testid="feature-gate-compact">
        <LockIcon className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>
            {displayFeatureName}は{PLAN_DISPLAY_NAMES[minimumRequiredPlan]}
            以上で利用可能です
          </span>
          <Button size="sm" onClick={handleUpgrade}>
            <UpgradeIcon className="mr-1 h-3 w-3" />
            アップグレード
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // 標準表示（カード形式）
  return (
    <>
      <Card className="bg-muted/50 text-center" data-testid="feature-gate">
        <CardContent className="p-6">
          <div className="mb-4 flex justify-center">
            <LockIcon className="h-12 w-12 text-muted-foreground" />
          </div>

          <h3 className="mb-1 text-lg font-semibold">
            {displayFeatureName}
          </h3>

          {featureDescription && (
            <p className="mb-4 text-sm text-muted-foreground">
              {featureDescription}
            </p>
          )}

          <Badge variant="outline" className="mb-6">
            {PLAN_DISPLAY_NAMES[minimumRequiredPlan]}以上で利用可能
          </Badge>

          <div className="mb-4 flex justify-center">
            <Button onClick={handleUpgrade}>
              <UpgradeIcon className="mr-2 h-4 w-4" />
              プランをアップグレード
            </Button>
          </div>

          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
            <InfoIcon className="h-3 w-3" />
            <span>
              現在のプラン: {PLAN_DISPLAY_NAMES[effectiveCurrentPlan]}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Issue #1590: UpgradePrompt ダイアログ - Stripe Checkout 統合 */}
      <UpgradePrompt
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        currentPlan={{ id: effectiveCurrentPlan }}
        recommendedPlan={
          minimumRequiredPlan as 'light' | 'standard' | 'premium'
        }
        feature={displayFeatureName}
      />
    </>
  );
};

/**
 * Feature Check Hook
 *
 * 機能アクセスをチェックするカスタムフック
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useFeatureAccess = (feature: string): boolean => {
  const currentPlan = useAuthStore((state) => state.currentPlan);
  const paidEntitlementBridge = usePaidEntitlementBridge();

  const requiredPlan =
    (
      FEATURE_PLAN_MAP as Record<
        string,
        'free' | 'light' | 'standard' | 'premium'
      >
    )[feature] || 'premium';
  // プランレベルを取得（Plan名は既に統一されているため直接使用）
  // Issue #1497: フォールバックを free (0) に変更
  const effectiveCurrentPlan = applyPaidEntitlementBridgeToPlan(
    currentPlan,
    paidEntitlementBridge
  );
  const currentPlanLevel = PLAN_HIERARCHY[effectiveCurrentPlan] || 0;
  const requiredPlanLevel = PLAN_HIERARCHY[requiredPlan];

  return currentPlanLevel >= requiredPlanLevel;
};

export default FeatureGate;
